// Manual product search/select for an unmatched or misidentified line
// (proforma-rules skill's Excel-import section: "Unmatched lines are
// normal... must still be fully quotable by hand" — this is the "by hand"
// tool). Deliberately never rewrites the line's description when a product
// is picked — only sets productId. request_lines.raw_description (and, for
// a line that already exists as a quotation_lines row, its own copied
// description) stays exactly as the buyer/sales user typed it; picking a
// catalog product only tags which product this line prices against for
// floor-price enforcement and the recent-quotes/list-floor reference.

import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useCatalogProducts } from '../hooks/useCatalogProducts.js';
import { useDebouncedValue } from '../utils/useDebouncedValue.js';
import { formatMoney } from 'shared/money.js';
import styles from './ProductPicker.module.css';

export default function ProductPicker({ selectedProduct, onSelect, onClear }) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);

  const { data: products, isFetching } = useCatalogProducts(
    debouncedQuery.trim() ? { search: debouncedQuery.trim() } : {}
  );

  const results = useMemo(() => (products ? Array.from(products.values()) : []), [products]);

  if (!open) {
    return (
      <div className={styles.summary}>
        {selectedProduct ? (
          <>
            <span className={styles.selectedName}>
              {lang === 'am' && selectedProduct.nameAm
                ? selectedProduct.nameAm
                : selectedProduct.name}
              {selectedProduct.sku ? ` · ${selectedProduct.sku}` : ''}
            </span>
            <button type="button" className={styles.linkButton} onClick={() => setOpen(true)}>
              {t('workspace.changeProduct')}
            </button>
            <button type="button" className={styles.linkButton} onClick={onClear}>
              {t('workspace.clearProduct')}
            </button>
          </>
        ) : (
          <button type="button" className={styles.pickButton} onClick={() => setOpen(true)}>
            <Search size={14} aria-hidden="true" />
            {t('workspace.pickProduct')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.searchRow}>
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('picker.searchPlaceholder')}
          className={styles.searchInput}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
            }
          }}
        />
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => setOpen(false)}
          aria-label={t('picker.cancel')}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {isFetching && <p className={styles.hint}>{t('picker.loading')}</p>}
      {!isFetching && results.length === 0 && (
        <p className={styles.hint}>{t('picker.noResults')}</p>
      )}

      {!isFetching && results.length > 0 && (
        <ul className={styles.results}>
          {results.slice(0, 20).map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className={styles.resultButton}
                onClick={() => {
                  onSelect(product);
                  setOpen(false);
                  setQuery('');
                }}
              >
                <span className={styles.resultName}>
                  {lang === 'am' && product.nameAm ? product.nameAm : product.name}
                  {product.sku ? ` · ${product.sku}` : ''}
                </span>
                <span className={styles.resultTiers}>
                  {product.tiers.length === 0 && t('workspace.reference.none')}
                  {product.tiers.map((tierRow) => (
                    <span key={tierRow.tier} className={`${styles.tierChip} tabularNums`}>
                      {t('picker.tierRow', {
                        tier: t(`workspace.reference.tier.${tierRow.tier}`),
                        list: formatMoney(tierRow.listPrice),
                        floor: formatMoney(tierRow.floorPrice),
                      })}
                    </span>
                  ))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
